import { Component, OnInit } from '@angular/core';
import { SeoService } from '../../shared/seo.service';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-icf-overview',
  templateUrl: './icf-overview.component.html',
  styleUrls: ['./icf-overview.component.scss']
})
export class IcfOverviewComponent implements OnInit {

  constructor(private seoService: SeoService) {}

  // ── ICF Credential data (kept in component for easy CMS-style updates) ────
  readonly credentials = [
    {
      badge: 'ACC',
      title: 'Associate Certified Coach',
      tag: 'Entry Level',
      featured: false,
      educationHours: 60,
      coachingHours: 100,
      mentorHours: 10,
      nexgenProgram: 'NEXGEN Gold program is your ACC pathway'
    },
    {
      badge: 'PCC',
      title: 'Professional Certified Coach',
      tag: 'Most Recognized',
      featured: true,
      educationHours: 125,
      coachingHours: 500,
      mentorHours: 10,
      nexgenProgram: 'NEXGEN Platinum program is your PCC pathway'
    },
    {
      badge: 'MCC',
      title: 'Master Certified Coach',
      tag: 'Elite Level',
      featured: false,
      educationHours: 200,
      coachingHours: 2500,
      mentorHours: 10,
      nexgenProgram: 'Our Director of Coaching Education, Lyssa deHart, holds the MCC'
    }
  ];

  // ── Programs summary ──────────────────────────────────────────────────────
  readonly programs = [
    {
      tier: 'SILVER',
      tierClass: 'silver-tier',
      name: 'Certificate',
      tagline: 'Try coaching first. Build real skills.',
      price: '$9,995',
      features: [
        '38 education hours',
        '2–3 months',
        'Non-ICF certificate',
        'AI-powered LMS',
        'Upgradeable to Gold'
      ],
      ctaText: 'Learn More',
      ctaClass: 'silver-cta',
      popular: false
    },
    {
      tier: 'GOLD ★',
      tierClass: 'gold-tier',
      name: 'ACC — Level 1',
      tagline: 'Get credentialed. Build your business.',
      price: '$11,995',
      features: [
        '69 ICF education hours',
        '10 hrs mentor coaching',
        '5–6 months',
        'Barter Coaching Platform',
        '1-year ICF Membership',
        'Business-building track'
      ],
      ctaText: 'Enroll in Gold',
      ctaClass: 'gold-cta',
      popular: true
    },
    {
      tier: 'PLATINUM',
      tierClass: 'platinum-tier',
      name: 'PCC — Level 2',
      tagline: 'Advanced ICF training for the PCC pathway.',
      price: '$13,995',
      features: [
        '125 ICF education hours',
        '10 hrs mentor coaching',
        '6–12 months',
        'Advanced elective courses',
        'PCC Mentoring & Eval',
        '1-year ICF Membership'
      ],
      ctaText: 'Learn More',
      ctaClass: 'platinum-cta',
      popular: false
    }
  ];

  // ── Comparison rows ───────────────────────────────────────────────────────
  readonly comparisonRows = [
    {
      feature: 'ICF-accredited training',
      sub: 'Required for all ICF credentials',
      competitor: 'Most have this',
      competitorIcon: 'check-yes',
      nexgen: 'Level 1 & Level 2',
      nexgenIcon: 'check-gold'
    },
    {
      feature: 'Business-building training',
      sub: 'How to find clients, price, and earn',
      competitor: 'Almost none include this',
      competitorIcon: 'check-no',
      nexgen: 'Full curriculum included',
      nexgenIcon: 'check-gold'
    },
    {
      feature: 'Designed for working professionals',
      sub: 'Evenings, weekends, self-paced',
      competitor: 'Some offer this',
      competitorIcon: 'check-partial',
      nexgen: 'Built entirely for this',
      nexgenIcon: 'check-gold'
    },
    {
      feature: 'Practice hours solution',
      sub: 'How to log required ICF coaching hours',
      competitor: 'You figure it out alone',
      competitorIcon: 'check-no',
      nexgen: 'Barter Coaching Platform',
      nexgenIcon: 'check-gold'
    },
    {
      feature: 'Rolling enrollment',
      sub: 'Start when you\'re ready',
      competitor: 'Usually cohort-based, wait months',
      competitorIcon: 'check-no',
      nexgen: 'Start anytime',
      nexgenIcon: 'check-gold'
    },
    {
      feature: 'Upgrade path',
      sub: 'Start Silver, move to Gold or Platinum',
      competitor: 'Rarely offered',
      competitorIcon: 'check-no',
      nexgen: 'Always available',
      nexgenIcon: 'check-gold'
    },
    {
      feature: 'ICF Membership included',
      sub: '1-year membership with Gold & Platinum',
      competitor: 'Extra cost',
      competitorIcon: 'check-no',
      nexgen: 'Included in Gold & Platinum',
      nexgenIcon: 'check-gold'
    }
  ];

  ngOnInit(): void {
     this.seoService.applySeo({
  title: 'ICF Certification Explained | ACC, PCC, MCC and the NEXGEN Path',
  description: 'Learn what ICF certification means, the difference between ACC, PCC, and MCC, the hours required for each credential, and which NEXGEN program supports each pathway.',
  keywords: 'ICF certification, ICF coaching credentials, ACC vs PCC, MCC credential, ICF Level 1 Level 2, NEXGEN Gold ACC, NEXGEN Platinum PCC',
  canonicalUrl: 'https://academy.nexgencoaches.com/icf-overview',
  ogTitle: 'ICF Certification Explained | ACC, PCC, MCC and the NEXGEN Path',
  ogDescription: 'Learn what ICF certification means, the difference between ACC, PCC, and MCC, the hours required for each credential, and which NEXGEN program supports each pathway.',
  ogType: 'website',
  twitterTitle: 'ICF Certification Explained | ACC, PCC, MCC and the NEXGEN Path',
  twitterDescription: 'Understand the ICF credential path and see how NEXGEN maps to ACC and PCC.',
  robots: 'index, follow',
  schema: [
    {
      '@type': 'WebPage',
      '@id': 'https://academy.nexgencoaches.com/icf-overview',
      'url': 'https://academy.nexgencoaches.com/icf-overview',
      'name': 'ICF Certification Explained | ACC, PCC, MCC and the NEXGEN Path',
      'description': 'Learn what ICF certification means, the difference between ACC, PCC, and MCC, the hours required for each credential, and which NEXGEN program supports each pathway.'
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'What is ICF coaching certification?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'ICF coaching certification is a professional credential path from the International Coaching Federation. The three main credentials are ACC, PCC, and MCC.'
          }
        },
        {
          '@type': 'Question',
          'name': 'What is the difference between ACC and PCC?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'ACC is the entry-level ICF credential. PCC is a more advanced credential that usually requires more training and more coaching hours.'
          }
        },
        {
          '@type': 'Question',
          'name': 'How many hours do I need for ACC?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'ACC requires 60 education hours, 100 coaching hours, and 10 mentor coaching hours.'
          }
        },
        {
          '@type': 'Question',
          'name': 'How many hours do I need for PCC?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'PCC requires 125 education hours, 500 coaching hours, and 10 mentor coaching hours.'
          }
        },
        {
          '@type': 'Question',
          'name': 'What is the difference between Level 1 and Level 2?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Level 1 is the ICF-accredited education path built for ACC. Level 2 is the larger ICF-accredited education path built for PCC.'
          }
        },
        {
          '@type': 'Question',
          'name': 'Which NEXGEN program supports ACC?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'NEXGEN Gold supports the ACC pathway.'
          }
        },
        {
          '@type': 'Question',
          'name': 'Which NEXGEN program supports PCC?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'NEXGEN Platinum supports the PCC pathway.'
          }
        },
        {
          '@type': 'Question',
          'name': 'Is Silver part of the ICF path?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Silver is a non-ICF certificate option. It is designed as a starting point and can be upgraded later.'
          }
        },
        {
          '@type': 'Question',
          'name': 'Does NEXGEN include mentor coaching?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Gold and Platinum include mentor coaching as part of the ICF-aligned pathway.'
          }
        }
      ]
    }
  ]
});

    // Scroll to top on load
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  ngOnDestroy(): void {
    this.seoService.clearSeo();
  }
}